import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output, TemplateRef } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CignaIconComponent } from '../../components/cigna-icon/cigna-icon.component';
import { AttachedFile } from '../../models/attached-file';
import { UtilsService } from '../../services/utils.service';

@Component({
  selector: 'app-file-attach-field',
  templateUrl: './file-attach-field.component.html',
  standalone: true,
  imports: [CommonModule, ButtonModule, CignaIconComponent, DragDropModule, TranslateModule],
})
export class FileAttachFieldComponent {
  private _utilsService = inject(UtilsService);

  @Input({ required: true }) title = '';
  @Input({ required: true }) subtitle = '';
  @Input() badgeLabel = '';
  @Input() badgeClass = 'form-fields--file-attach--badge--required destacado-error-border';
  @Input() appButtonLabel = '';
  @Input() selectFileLabel = '';
  @Input() dropzoneTitle = '';
  @Input() formatHint = '';
  @Input() files: AttachedFile[] = [];
  @Input() accept = '.jpg,.jpeg,.png,.pdf';
  @Input() inputId = 'fileAttachInput';
  @Input() enableReorder = true;
  @Input() hostClass = '';
  @Input() showUploadOnlyWhenEmpty = false;
  @Input() showAddPrescription = false;
  @Input() fileDetailTemplate?: TemplateRef<any>;
  @Input() itemErrorIndexes: number[] = [];
  @Input() itemErrorClass = '';

  @Output() filesSelected = new EventEmitter<File[]>();
  @Output() filesRejected = new EventEmitter<File[]>();
  @Output() fileRemoved = new EventEmitter<number>();
  @Output() filesReordered = new EventEmitter<CdkDragDrop<AttachedFile[]>>();
  @Output() appUploadRequested = new EventEmitter<void>();
  @Output() addPrescription = new EventEmitter<void>();

  dropActive = false;
  readonly isDesktopWeb: boolean =
    !this._utilsService.isApp() &&
    !this._utilsService.isMobile() &&
    !this._utilsService.isTablet();

  fileSize(size: number): string {
    return this._utilsService.fileSize(size);
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.emitIncomingFiles(Array.from(input.files));
      input.value = '';
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropActive = false;
    if (!this.isDesktopWeb) {
      return;
    }
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.emitIncomingFiles(Array.from(files));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isDesktopWeb) {
      this.dropActive = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dropActive = false;
  }

  onDropListDropped(event: CdkDragDrop<AttachedFile[]>): void {
    this.filesReordered.emit(event);
  }

  openFileDialog(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  onAppUploadClick(): void {
    this.appUploadRequested.emit();
  }

  private emitIncomingFiles(files: File[]): void {
    if (this.showUploadOnlyWhenEmpty && this.files.length) {
      return;
    }
    const selectedFiles = files.slice(0, 1);
    const acceptedFiles = this.filterAllowedFiles(selectedFiles);
    const rejectedFiles = selectedFiles.filter(file => !this.isAllowedByAccept(file));
    if (acceptedFiles.length) {
      this.filesSelected.emit(acceptedFiles);
    }
    if (rejectedFiles.length) {
      this.filesRejected.emit(rejectedFiles);
    }
  }

  private filterAllowedFiles(files: File[]): File[] {
    return files.filter(file => this.isAllowedByAccept(file));
  }

  private isAllowedByAccept(file: File): boolean {
    const acceptTokens = this.accept
      .split(',')
      .map(token => token.trim().toLowerCase())
      .filter(Boolean);

    if (!acceptTokens.length) {
      return true;
    }

    const fileName = file.name.toLowerCase();
    const fileType = (file.type || '').toLowerCase();
    const extension = fileName.includes('.') ? `.${fileName.split('.').pop()}` : '';

    return acceptTokens.some(token => {
      if (token.startsWith('.')) {
        return extension === token;
      }
      if (token.endsWith('/*')) {
        return !!fileType && fileType.startsWith(token.replace('/*', '/'));
      }
      return !!fileType && fileType === token;
    });
  }
}
